import {useConnectorsResults} from "@feature/surveys/hooks/useConnectorsResults";
import useCountryAxiosInstance from "@hooks/useCountryAxiosInstance";
import useAsyncError from "@hooks/useAsyncError";
import {ServicesConfigurationsWithIdSchema, ServiceSurveyResponsesWithIdSchema} from "@feature/types";
import {useEffect, useState} from "react";
import {DesignSurveyInfo} from "@feature/surveys/types";
import {extractConnectorResponses} from "@feature/surveys/lib/connectors/utils";
import convertSurveyResponses from "@utils/convertSurveyResponses";
import prepareConfigurationStep from "@utils/prepareConfigurationStep";
import TimbertechWoodMaterialsCSV from "feature/services/connettori/assets/timbertech-wood-materials.csv";
import TimbertechCACSV from "feature/services/connettori/assets/timbertech-ca.csv";
import {useCSV} from "@feature/commons/hooks/useCSV";

export const useDesignReportsDownloader = (worksiteId: number) => {

  // hooks
  const countryApiRequest = useCountryAxiosInstance();
  const throwError = useAsyncError();
	const {download: downloadConnectorReport} = useConnectorsResults();
	const timbertechWoodMaterials = useCSV(TimbertechWoodMaterialsCSV);
  const timbertechCA = useCSV(TimbertechCACSV);

	// state
	const [available, setAvailable]
		= useState<DesignSurveyInfo[]>();
	const [loading, setLoading] = useState(false);

	// utilities
	const getResponses = async (surveyId: number) => (
		await countryApiRequest!.get("/service_survey_responses/all", {params: {service_survey_id: surveyId}})
	) as ServiceSurveyResponsesWithIdSchema[];
	const getConfigurations = async (serviceId: number) => (
		await countryApiRequest!.get(
			"/services_configurations/all",
			{params: {
				service_id: serviceId,
				join: "product,system",
				active: true,
			}}
		)
	) as ServicesConfigurationsWithIdSchema[];

  // handlers
	const getAvailableSurveys = async () => {
		setLoading(true);
		try {
			return (await countryApiRequest!.get(`/service_surveys/design/${worksiteId}`)) as DesignSurveyInfo[];
		}
		catch (e) {
			throwError(e);
			return undefined;
		}
		finally {
			setLoading(false);
		}
	}
	const download = async (surveyIds: number[]) => {
		if (!available)
			throw new Error("The available design surveys have not been retrieved yet");
		available.sort((s1, s2)=> {
			if (s1.service_id === s2.service_id) {
				return s1.id - s2.id; // Sort by ID if service IDs are the same
			}
			return s1.service_id - s2.service_id; // Sort by service ID otherwise
		})
		const toDownload = available.filter((survey:DesignSurveyInfo) => survey.complete && surveyIds.includes(survey.id));
		try {

			// 1. Report surveys that are missing
			const missing = surveyIds.filter(
				(sid) => !toDownload.find((survey) => survey.id === sid)
			);
			if (missing.length > 0)
				console.warn(`The following surveys are missing: ${missing.join(", ")}`);

			// 2. Iterate over the surveys and call the right download function based on their subtype
			// const blobs: Record<number, Blob | null> = {};
			const blobs: (Blob | null)[] = [];
			const configurationsCache: Record<number, ServicesConfigurationsWithIdSchema[]> = {}
			for (const entry of toDownload) {
				const {id, service_id, type, subtype, name} = entry;
				switch (type) {
					case "connectors":

						if (!subtype) {
							console.warn(`The survey ${id} is a connector survey but has no subtype - it will be ignored`);
							continue;
						}

						// 2.1 Retrieve data needed to download the report (responses, configurations, async results)
						const [
							responses,
							configuration,
							result,
						] = await Promise.all([
							getResponses(id),
							!configurationsCache[service_id]
								? getConfigurations(service_id)
								: new Promise<ServicesConfigurationsWithIdSchema[]>((resolve) => resolve(configurationsCache[service_id])),
							countryApiRequest!.get(`/connectors/${id}`) as Promise<{[key: string]: any}>,
						]);
						// Update configurations cache
						if (!configurationsCache[service_id])
							configurationsCache[service_id] = configuration;

						// 2.2 Preprocess the data to make it compatible with the download function
						const calculationOutput = configuration.find((c) =>
							c.type == "output"
							&& c.output_type == "calculation"
							&& c.calculation?.length == 1
							&& c.calculation[0].startsWith("{")
							&& JSON.parse(c.calculation[0]).original === "connector"
						)
						if (!calculationOutput) {
							console.warn(`The survey ${id} has no calculation output - it will be ignored`);
							continue;
						}
						const data = prepareConfigurationStep(configuration);
						const newResponses = convertSurveyResponses(responses, {data, configuration});

						// 2.3 Call the download function
						if (!timbertechWoodMaterials || !timbertechCA) {
							console.warn(`The survey ${id} has no timbertech materials - it will be ignored`);
							continue;
						}
						blobs.push(await downloadConnectorReport(
							extractConnectorResponses(calculationOutput, newResponses, {timbertechWoodMaterials, timbertechCA}),
							result[subtype],
							subtype as "diaphragm" | "vertical" | "wood",
							true,
							name,
						) as Blob | null);

						break;
					default:
						console.warn(`The survey ${id} has an unknown type ${type} - it will be ignored`);
						break;
				}
			}
			return blobs;
		}
		catch (e) {

			throwError(e);
			return null;
		}
	}

	useEffect(() => {
		// Whenever the worksite changes, retrieve the IDs of the design surveys that are available for download
		getAvailableSurveys().then((res) => {
			setAvailable(res?.filter(({type}) => !!type));
		});
	}, [worksiteId]);  // eslint-disable-line react-hooks/exhaustive-deps

  return {available, download, loading};

}